/**
 * Welle 14c — Lese-Helper für `v5_ai_research_history`.
 *
 * Liefert Snapshot-Reihen pro Symbol für den Sentiment-Drift-Chart auf der
 * Symbol-Detail-Seite. Quelle wird täglich 23:00 Europe/Berlin vom Cron
 * `snapshot-ai-research` gefüttert.
 */

import { logCaughtError } from './error-log';
import { supabase } from './supabase';

export interface SentimentHistoryRow {
  symbol: string;
  snapshot_date: string; // 'YYYY-MM-DD'
  analyst_buy_ratio: number | null;
  analyst_upside_pct: number | null;
  news_sentiment_net: number | null;
  fh_news_count_7d: number | null;
  ai_fund_net_buying_pct: number | null;
}

/**
 * Holt die letzten N Snapshot-Tage für ein Symbol. Default 30 Tage.
 * Sortiert aufsteigend (ältester zuerst) damit Charts links→rechts laufen.
 */
export async function fetchSentimentHistory(
  symbol: string,
  days: number = 30,
  signal?: AbortSignal
): Promise<SentimentHistoryRow[]> {
  if (signal?.aborted) return [];
  const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  const { data, error } = await supabase
    .from('v5_ai_research_history')
    .select(
      'symbol, snapshot_date, analyst_buy_ratio, analyst_upside_pct, news_sentiment_net, fh_news_count_7d, ai_fund_net_buying_pct'
    )
    .eq('symbol', symbol.toUpperCase())
    .gte('snapshot_date', since)
    .order('snapshot_date', { ascending: true })
    .returns<SentimentHistoryRow[]>();
  if (error) {
    logCaughtError('ai-research-history.fetchSentimentHistory', error, {
      severity: 'warn',
      context: { symbol, days },
    });
    return [];
  }
  return data ?? [];
}

export interface DriftSummary {
  firstSeen: string | null;
  lastSeen: string | null;
  snapshotCount: number;
  analystBuyDeltaPp: number | null;        // Punkte (z.B. +5 = +5 pp Anstieg)
  analystUpsideDeltaPct: number | null;
  sentimentNetDeltaPp: number | null;
  newsCount7dCurrent: number | null;
}

// =============================================================
// Welle 14e — Sentiment-Drift-Alerts für die Watchlist
// =============================================================

export type DriftSeverity = 'positive' | 'negative' | 'none';

export interface SymbolDriftAlert {
  symbol: string;
  severity: DriftSeverity;
  daysCompared: number;
  summary: string;
  /** Drei Delta-Werte (oder null wenn Sub-Signal fehlte). */
  analystBuyDeltaPp: number | null;
  analystUpsideDeltaPct: number | null;
  sentimentNetDeltaPp: number | null;
}

/**
 * Batch-Load der letzten ~7d Snapshots für viele Symbole in 1 Query.
 * Aggregiert anschließend pro Symbol zum DriftAlert.
 */
export async function fetchSentimentAlertsBatch(
  symbols: string[],
  lookbackDays: number = 7,
  signal?: AbortSignal
): Promise<Map<string, SymbolDriftAlert>> {
  if (signal?.aborted || symbols.length === 0) return new Map();
  const upperSymbols = symbols.map((s) => s.toUpperCase());
  const since = new Date(Date.now() - lookbackDays * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  const { data, error } = await supabase
    .from('v5_ai_research_history')
    .select(
      'symbol, snapshot_date, analyst_buy_ratio, analyst_upside_pct, news_sentiment_net'
    )
    .in('symbol', upperSymbols)
    .gte('snapshot_date', since)
    .order('snapshot_date', { ascending: true });
  if (error) {
    logCaughtError('ai-research-history.fetchSentimentAlertsBatch', error, {
      severity: 'warn',
      context: { symbol_count: symbols.length },
    });
    return new Map();
  }
  // Pro Symbol gruppieren + Drift berechnen
  type Row = {
    symbol: string;
    snapshot_date: string;
    analyst_buy_ratio: number | null;
    analyst_upside_pct: number | null;
    news_sentiment_net: number | null;
  };
  const grouped = new Map<string, Row[]>();
  for (const row of (data ?? []) as Row[]) {
    const sym = row.symbol.toUpperCase();
    const arr = grouped.get(sym) ?? [];
    arr.push(row);
    grouped.set(sym, arr);
  }
  const result = new Map<string, SymbolDriftAlert>();
  for (const [sym, rows] of grouped.entries()) {
    if (rows.length < 2) continue;
    const first = rows[0]!;
    const last = rows[rows.length - 1]!;
    const buyDelta =
      first.analyst_buy_ratio !== null && last.analyst_buy_ratio !== null
        ? (last.analyst_buy_ratio - first.analyst_buy_ratio) * 100
        : null;
    const upsideDelta =
      first.analyst_upside_pct !== null && last.analyst_upside_pct !== null
        ? last.analyst_upside_pct - first.analyst_upside_pct
        : null;
    const sentDelta =
      first.news_sentiment_net !== null && last.news_sentiment_net !== null
        ? last.news_sentiment_net - first.news_sentiment_net
        : null;
    // Schwellen: deutlich = ≥10pp Buy, ≥5% Upside, ≥15pp Sentiment
    const positiveTriggers: string[] = [];
    const negativeTriggers: string[] = [];
    if (buyDelta !== null) {
      if (buyDelta >= 10) positiveTriggers.push(`Buy-Ratio +${buyDelta.toFixed(0)} pp`);
      else if (buyDelta <= -10) negativeTriggers.push(`Buy-Ratio ${buyDelta.toFixed(0)} pp`);
    }
    if (upsideDelta !== null) {
      if (upsideDelta >= 5) positiveTriggers.push(`Upside +${upsideDelta.toFixed(1)} %`);
      else if (upsideDelta <= -5) negativeTriggers.push(`Upside ${upsideDelta.toFixed(1)} %`);
    }
    if (sentDelta !== null) {
      if (sentDelta >= 15) positiveTriggers.push(`Sentiment +${sentDelta.toFixed(0)} pp`);
      else if (sentDelta <= -15) negativeTriggers.push(`Sentiment ${sentDelta.toFixed(0)} pp`);
    }
    const severity: DriftSeverity =
      negativeTriggers.length > positiveTriggers.length
        ? 'negative'
        : positiveTriggers.length > 0
          ? 'positive'
          : 'none';
    if (severity === 'none') continue;
    const triggers = severity === 'positive' ? positiveTriggers : negativeTriggers;
    result.set(sym, {
      symbol: sym,
      severity,
      daysCompared: rows.length,
      summary: triggers.slice(0, 2).join(', '),
      analystBuyDeltaPp: buyDelta,
      analystUpsideDeltaPct: upsideDelta,
      sentimentNetDeltaPp: sentDelta,
    });
  }
  return result;
}

/**
 * Welle 14d — Full-History für Backtest-Lauf.
 * Lädt ALLE Snapshots für ein Symbol (kein Limit), aufsteigend sortiert.
 */
export async function fetchFullSentimentHistoryForBacktest(
  symbol: string,
  signal?: AbortSignal
): Promise<SentimentHistoryRow[]> {
  if (signal?.aborted) return [];
  const { data, error } = await supabase
    .from('v5_ai_research_history')
    .select(
      'symbol, snapshot_date, analyst_buy_ratio, analyst_upside_pct, news_sentiment_net, fh_news_count_7d, ai_fund_net_buying_pct'
    )
    .eq('symbol', symbol.toUpperCase())
    .order('snapshot_date', { ascending: true })
    .returns<SentimentHistoryRow[]>();
  if (error) {
    logCaughtError('ai-research-history.fetchFullSentimentHistoryForBacktest', error, {
      severity: 'warn',
      context: { symbol },
    });
    return [];
  }
  return data ?? [];
}

/**
 * Drift-Zusammenfassung: Differenz erstes vs. letztes Datenpunkt für die
 * 3 Kernfelder. Liefert null wenn weniger als 2 Snapshots.
 */
export function summarizeDrift(rows: SentimentHistoryRow[]): DriftSummary {
  if (rows.length === 0) {
    return {
      firstSeen: null,
      lastSeen: null,
      snapshotCount: 0,
      analystBuyDeltaPp: null,
      analystUpsideDeltaPct: null,
      sentimentNetDeltaPp: null,
      newsCount7dCurrent: null,
    };
  }
  const first = rows[0]!;
  const last = rows[rows.length - 1]!;

  function delta(a: number | null, b: number | null): number | null {
    if (a === null || b === null) return null;
    return b - a;
  }

  return {
    firstSeen: first.snapshot_date,
    lastSeen: last.snapshot_date,
    snapshotCount: rows.length,
    analystBuyDeltaPp:
      first.analyst_buy_ratio === null || last.analyst_buy_ratio === null
        ? null
        : (last.analyst_buy_ratio - first.analyst_buy_ratio) * 100,
    analystUpsideDeltaPct: delta(first.analyst_upside_pct, last.analyst_upside_pct),
    sentimentNetDeltaPp: delta(first.news_sentiment_net, last.news_sentiment_net),
    newsCount7dCurrent: last.fh_news_count_7d,
  };
}
